/**
 * Catalogue métiers unifié (signup / profil / wizard / Booking).
 * Slugs = `professions.slug` (SEO stable). Visibilité ≠ suppression DB.
 */

export type ProfessionCatalogEntry = {
  id: number;
  slug: string;
  /** Affichable à l’ajout (wizard / profil). */
  selectable: boolean;
  /** Cœur marketplace / signup activités. */
  core: boolean;
  /** Lié au module pension (FR-only). */
  boarding?: boolean;
  /** Alias logique (ex. hôtel → pension) — masqué, non sélectionnable. */
  aliasOf?: string;
};

/**
 * Source de vérité IDs/slugs (sync table `professions`).
 * Masqués : hôtel (doublon pension), asso/refuge/zoothérapeute/éleveur (quasi 0 usage),
 * education-feline (proxy espèce Chat — espèce au service).
 */
export const PROFESSION_CATALOG: readonly ProfessionCatalogEntry[] = [
  { id: 1, slug: 'trainers', selectable: true, core: true },
  { id: 2, slug: 'behaviorist', selectable: true, core: true },
  { id: 3, slug: 'pet-sitter', selectable: true, core: true },
  { id: 4, slug: 'dog-walker', selectable: true, core: true },
  { id: 5, slug: 'groomer', selectable: true, core: true },
  { id: 6, slug: 'osteopath', selectable: true, core: false },
  { id: 8, slug: 'nutriotionist', selectable: true, core: false }, // typo historique slug DB
  { id: 9, slug: 'animal-communicator', selectable: true, core: false },
  { id: 10, slug: 'loisir-canin', selectable: true, core: true },
  { id: 11, slug: 'naturopathe', selectable: true, core: false },
  { id: 12, slug: 'sport-canin', selectable: true, core: true },
  { id: 13, slug: 'eleveur', selectable: false, core: false },
  { id: 14, slug: 'pension', selectable: true, core: true, boarding: true },
  {
    id: 15,
    slug: 'hotel-pour-animaux',
    selectable: false,
    core: false,
    boarding: true,
    aliasOf: 'pension',
  },
  { id: 16, slug: 'taxi-animalier', selectable: true, core: false },
  { id: 17, slug: 'association', selectable: false, core: false },
  { id: 18, slug: 'refuge', selectable: false, core: false },
  { id: 19, slug: 'photographe-animalier', selectable: true, core: false },
  { id: 20, slug: 'zootherapeute', selectable: false, core: false },
  /** Proxy espèce (Chat) — masqué ; espèce vit sur le service. */
  { id: 21, slug: 'education-feline', selectable: false, core: false },
  { id: 22, slug: 'secourisme-animalier', selectable: true, core: false },
  { id: 23, slug: 'massage-animalier', selectable: true, core: false },
] as const;

const bySlug = new Map(PROFESSION_CATALOG.map((e) => [e.slug, e]));

/** Slugs legacy SEO / typos → slug canonique DB. */
const SLUG_ALIASES: Record<string, string> = {
  'pension-canine': 'pension',
  Osteopath: 'osteopath',
  osteopath: 'osteopath',
  masseur: 'massage-animalier',
  'masseur-canin': 'massage-animalier',
};

export function normalizeProfessionSlug(
  slug?: string | null
): string | null {
  if (!slug) return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;
  const aliased = SLUG_ALIASES[trimmed] ?? SLUG_ALIASES[trimmed.toLowerCase()];
  if (aliased) return aliased;
  return trimmed.toLowerCase();
}

export function getProfessionCatalogEntry(
  slug?: string | null
): ProfessionCatalogEntry | undefined {
  const norm = normalizeProfessionSlug(slug);
  if (!norm) return undefined;
  return bySlug.get(norm);
}

export function isProfessionSelectable(slug?: string | null): boolean {
  const entry = getProfessionCatalogEntry(slug);
  // Métiers hors catalogue (futurs) : visibles par défaut
  if (!entry) return true;
  return entry.selectable;
}

export function isProfessionCore(slug?: string | null): boolean {
  return Boolean(getProfessionCatalogEntry(slug)?.core);
}

export function isCatalogBoardingProfession(slug?: string | null): boolean {
  const entry = getProfessionCatalogEntry(slug);
  if (entry) return Boolean(entry.boarding);
  return normalizeProfessionSlug(slug) === 'pension';
}

export type ProfessionLike = {
  id: number;
  slug?: string | null;
  name?: string | null;
};

/**
 * Filtre la liste « Ajouter un métier ».
 * Conserve les IDs déjà liés au pro (même si masqués).
 */
export function filterSelectableProfessions<T extends ProfessionLike>(
  professions: T[],
  keepIds: readonly number[] = []
): T[] {
  const keep = new Set(keepIds);
  return professions.filter(
    (p) => keep.has(p.id) || isProfessionSelectable(p.slug)
  );
}

/** IDs cœur (signup / projection SEO prioritaire). */
export function coreProfessionIds(): number[] {
  return PROFESSION_CATALOG.filter((e) => e.core).map((e) => e.id);
}
